import { useEffect, useState } from 'react'
import { Keyboard, Platform } from 'react-native'
import { tlFallback } from 'utils/i18nUtils'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { Collapsible } from '@ui/animated/Collapsible'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import EmptyResult from '@ui/components/EmptyResult'
import Page from '@shell/Page'
import { FlashList } from '@shopify/flash-list'
import { CustomerCard } from '@user/customer/TileUser'
import { trpc } from 'api'
import { router, useLocalSearchParams } from 'expo-router'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import { useToast } from '@ui/toasts/useToastManager'
import CustomerAuthModal from '@user/CustomerAuthModal'
import StaffCustomerLoginModal from '@user/StaffCustomerLoginModal'
import { useStaffLoginCustomer } from 'hooks/useStaffLoginCustomer'
import { useCustomer } from 'hooks/useSession'
import Header from '@ui/elements/Header'
import Filters from './Filters'
import { Customer } from '@ca/common/types'

export default function StaffCustomers() {
  const { t, i18n } = useTranslation(['staff'])
  const text = 'staff:customers'
  const toast = useToast()
  const { q } = useLocalSearchParams<{
    q: string
  }>()

  const {
    data,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isInitialLoading,
    isFetching,
    isFetched,
    isRefetching,
    isError,
    error,
  } = trpc.customer.list.useInfiniteQuery(
    {
      query: q || '',
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },

      placeholderData: (v) => v,
      refetchOnWindowFocus: false,
      enabled: !!(q || '').trim(),
    }
  )

  useEffect(() => {
    if (!error) return
    if (error.data?.code === 'UNAUTHORIZED') {
      // Default to global handler
      return
    }
    const key = error.data?.code?.toLowerCase() || 'unknown'
    toast({
      variant: 'error',
      title: t('toasts:error.default.title'),
      text: tlFallback(
        i18n,
        `toasts:error.${key}.text`,
        'toasts:error.default.desc'
      ),
    })
  }, [error, t, i18n, toast])

  const isLoadings =
    (isFetching || isInitialLoading || isLoading) && !isFetchingNextPage

  const showLoading = isLoadings && !!q
  const showEmpty = !isLoadings && !!q && isFetched && !isError
  const showItems = isFetched && !isLoadings

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  const flatData = useFlattenInfiniteData(data)
  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    refetch,
    isRefetching
  )
  const verify = useStaffLoginCustomer()
  const [authCustomer, setAuthCustomer] = useState<Customer | null>(null)
  const sessionCustomer = useCustomer()

  useEffect(() => {
    if (verify.result) {
      setAuthCustomer(null)
    }
  }, [verify.result])

  return (
    <>
      <Page stage="staff" list>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <Filters isFetching={isLoadings} />
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={isFetchingNextPage ? LoadingFooter : null}
          data={flatData}
          numColumns={1}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          onEndReachedThreshold={0.25}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          contentContainerClassName="pb-8"
          onEndReached={hasNextPage ? fetchNextPage : undefined}
          renderItem={({ item }) => (
            <Animated.View style={styleItems} className="px-8 pt-8">
              <CustomerCard
                data={item}
                onPress={() => {
                  Keyboard.dismiss()
                  if (sessionCustomer?.id === item.id) {
                    router.navigate('/customer/home')
                    return
                  }
                  setAuthCustomer(item)
                }}
              />
            </Animated.View>
          )}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <EmptyResult
                title={t(`${text}.empty.result.title`)}
                desc={t(`${text}.empty.result.desc`)}
                button={
                  Platform.OS === 'web'
                    ? undefined
                    : {
                        variant: 'outlined',
                        label: t('ui:reset'),
                        onPress: () => router.setParams({ q: '' }),
                      }
                }
              />
            </AnimatedEmpty>
          }
        />
        <CustomerAuthModal
          customer={authCustomer}
          onClose={() => {
            setAuthCustomer(null)
            if (!verify.result) {
              verify.cancel()
            }
          }}
          onSelectStrategy={(customer, strategy) => {
            verify.request(customer, strategy)
          }}
          isVerifying={verify.isLoading}
        />
        <StaffCustomerLoginModal
          verify={verify}
          onSuccess={() => verify.cancel()}
        />
      </Page>
      {/* <DevStatuses
        booleans={{
          isLoading,
          isFetching,
          isRefetching,
          isFetchingNextPage,
          isRefetchingByUser,
          isSuccess,
          isFetched,
          isError,
          isLoadingError,
          isInitialLoading,
        }}
      /> */}
    </>
  )
}
